/**
 * DataTransferTab — Story 1.6 AC-3 (Default-Tab der Hotspot-Detailseite)
 *
 * Referenz: claude-design-import/hotspot-detail.html (Panel „Data Transfer").
 * Vier Karten:
 *  1. GB-Verlauf · 30 Tage (Balken-Chart aus mobile/analytics/summary.series)
 *  2. Carrier-Split · 30 T (CarrierBreakdownStack aus carrier_breakdown)
 *  3. Geräte-$/GB vs. Netzwerk-Rate (EarnRateGauge-Muster; Referenzrate =
 *     Account-Durchschnitt ohne Hotspot-Filter — der Contract hat keinen
 *     separaten Netzwerk-Raten-Endpunkt)
 *  4. Unique-Connections-Meter mit Schwelle 25 (HIP-134) aus dem
 *     unique_connections-Block des Detail-Response
 */
import { useTranslation } from 'react-i18next'
import {
  CarrierBreakdownStack,
  EarnRateGauge,
  formatNumber,
  type CarrierSegment,
} from '../../components/ui'
import {
  useHotspotTransferSummary,
  useNetworkTransferSummary,
  useHotspotCarrierBreakdown,
  type TransferSummary,
} from './useMobileTransfer'
import type { HotspotDetail } from './useHotspotDetail'
import './hotspot-detail-tabs.css'

// HIP-134: Default-Schwelle für Offload-Qualifikation
const HIP134_THRESHOLD = 25

type Props = {
  hotspot: HotspotDetail
}

// --- GB-Verlauf (Balken-Chart, SVG) ---

const CHART_W = 640
const CHART_H = 220
const PAD_X = 36
const PAD_Y = 22

function GbBarChart({ series, total }: { series: TransferSummary['series']; total: number }) {
  const { t } = useTranslation('hotspotDetailTabs')
  const values = series.map((s) => (Number.isFinite(s.gb) && s.gb > 0 ? s.gb : 0))
  const max = Math.max(...values, 0.0001)
  const bw = (CHART_W - 2 * PAD_X) / Math.max(values.length, 1)
  const totalText = formatNumber(total, 1)

  return (
    <div className="htdt-chart-scroll">
      <svg
        className="htdt-chart"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={t('transfer.gbHistory.ariaLabel', { total: totalText })}
        data-testid="gb-history-chart"
      >
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={PAD_X}
            x2={CHART_W - PAD_X}
            y1={PAD_Y + f * (CHART_H - 2 * PAD_Y)}
            y2={PAD_Y + f * (CHART_H - 2 * PAD_Y)}
            stroke="var(--border)"
            strokeDasharray="2 4"
          />
        ))}
        {values.map((v, i) => {
          const bh = (v / max) * (CHART_H - 2 * PAD_Y)
          return (
            <rect
              key={series[i]?.bucket ?? i}
              x={PAD_X + i * bw + 1.5}
              y={CHART_H - PAD_Y - bh}
              width={Math.max(bw - 3, 1)}
              height={bh}
              rx={1.5}
              fill="var(--primary)"
              opacity={0.45 + 0.55 * (v / max)}
            />
          )
        })}
      </svg>
    </div>
  )
}

// --- Unique-Connections-Meter (HIP-134) ---

function UniqueConnectionsMeter({
  connections,
}: {
  connections: NonNullable<HotspotDetail['unique_connections']>
}) {
  const { t } = useTranslation('hotspotDetailTabs')
  const threshold = connections.threshold ?? HIP134_THRESHOLD
  const count = connections.count

  if (!connections.applicable) {
    return (
      <p className="muted" style={{ fontSize: 13 }} data-testid="connections-not-applicable">
        {t('transfer.connections.notApplicable')}
      </p>
    )
  }

  if (count == null) {
    return (
      <p className="muted" style={{ fontSize: 13 }} data-testid="connections-no-data">
        {t('transfer.connections.noData')}
      </p>
    )
  }

  const met = connections.threshold_met ?? count > threshold
  const scaleMax = Math.max(Math.ceil(threshold * 2.4), Math.ceil(count * 1.1))
  const fillPct = Math.min((count / scaleMax) * 100, 100)
  const thresholdPct = Math.min((threshold / scaleMax) * 100, 100)

  return (
    <div data-testid="unique-connections-meter">
      <div className="htdt-conn-value tnum">
        {formatNumber(count, 0)}{' '}
        <span
          className="htdt-conn-badge"
          data-met={met ? 'true' : 'false'}
          data-testid="connections-qualification"
        >
          {met ? `✓ ${t('transfer.connections.qualified')}` : t('transfer.connections.notQualified')}
        </span>
      </div>
      <div
        className="htdt-conn-meter"
        role="img"
        aria-label={t('transfer.connections.meterAriaLabel', {
          count: formatNumber(count, 0),
          threshold: formatNumber(threshold, 0),
        })}
      >
        <div className="htdt-conn-track">
          <div
            className="htdt-conn-fill"
            data-met={met ? 'true' : 'false'}
            style={{ width: `${fillPct}%` }}
          />
        </div>
        <div className="htdt-conn-threshold" style={{ left: `${thresholdPct}%` }} />
        <div className="htdt-conn-threshold-label" style={{ left: `${thresholdPct}%` }}>
          {t('transfer.connections.thresholdMarker', { threshold: formatNumber(threshold, 0) })}
        </div>
      </div>
      <div className="htdt-conn-scale" aria-hidden="true">
        <span>0</span>
        <span>{formatNumber(scaleMax, 0)}</span>
      </div>
      <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        {t('transfer.connections.basisNote', { hours: connections.window_hours ?? 24 })}
      </p>
    </div>
  )
}

// --- Tab ---

export function DataTransferTab({ hotspot }: Props) {
  const { t } = useTranslation('hotspotDetailTabs')
  const hotspotId = hotspot.id ?? null
  const summary = useHotspotTransferSummary(hotspotId, '30d')
  const network = useNetworkTransferSummary('30d')
  const carriers = useHotspotCarrierBreakdown(hotspotId, '30d')

  const deviceRate = summary.data?.avg_usd_per_gb ?? null
  const networkRate = network.data?.avg_usd_per_gb ?? null

  const carrierSegments: CarrierSegment[] = (carriers.data?.carriers ?? []).map((c) => ({
    id: c.carrier_id,
    label: c.label,
    value: c.total_gb,
  }))

  return (
    <div data-testid="data-transfer-tab">
      <div className="htdt-grid">
        {/* GB-Verlauf */}
        <div className="card card-pad">
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('transfer.gbHistory.heading')}
          </h3>
          <p className="htdt-card-subtitle">{t('transfer.gbHistory.subtitle')}</p>
          {summary.isLoading ? (
            <div className="skeleton" style={{ height: 220 }} aria-busy="true" />
          ) : summary.isError ? (
            <p className="muted" style={{ fontSize: 13 }} data-testid="gb-history-error">
              {t('transfer.gbHistory.error')}
            </p>
          ) : !summary.data || summary.data.series.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }} data-testid="gb-history-empty">
              {t('transfer.gbHistory.empty')}
            </p>
          ) : (
            <>
              <GbBarChart series={summary.data.series} total={summary.data.total_gb} />
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                {t('transfer.gbHistory.totalLabel', {
                  total: formatNumber(summary.data.total_gb, 1),
                })}
              </p>
            </>
          )}
        </div>

        {/* Carrier-Split */}
        <div className="card card-pad">
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('transfer.carrier.heading')}
          </h3>
          <p className="htdt-card-subtitle">{t('transfer.carrier.subtitle')}</p>
          {carriers.isLoading ? (
            <div className="skeleton" style={{ height: 160 }} aria-busy="true" />
          ) : carriers.isError ? (
            <p className="muted" style={{ fontSize: 13 }} data-testid="carrier-error">
              {t('transfer.carrier.error')}
            </p>
          ) : (
            <CarrierBreakdownStack
              segments={carrierSegments}
              formatValue={(v) => t('transfer.carrier.valueUnit', { value: formatNumber(v, 1) })}
              data-testid="carrier-breakdown"
            />
          )}
        </div>
      </div>

      <div className="htdt-grid-2">
        {/* Geräte-$/GB vs. Netzwerk-Rate */}
        <div className="card card-pad">
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('transfer.rate.heading')}
          </h3>
          <p className="htdt-card-subtitle">{t('transfer.rate.subtitle')}</p>
          {summary.isLoading || network.isLoading ? (
            <div className="skeleton" style={{ height: 140 }} aria-busy="true" />
          ) : summary.isError || network.isError ? (
            <p className="muted" style={{ fontSize: 13 }} data-testid="rate-error">
              {t('transfer.rate.error')}
            </p>
          ) : deviceRate == null || networkRate == null || networkRate <= 0 ? (
            <p className="muted" style={{ fontSize: 13 }} data-testid="rate-unavailable">
              {t('transfer.rate.unavailable')}
            </p>
          ) : (
            <>
              <EarnRateGauge
                value={deviceRate}
                base={networkRate}
                floor={networkRate * 0.5}
                cap={networkRate * 3}
                networkRate={networkRate}
                data-testid="device-rate-gauge"
              />
              <div className="htdt-legend">
                <span className="htdt-legend-item">
                  <span className="htdt-legend-dot" style={{ background: 'var(--primary)' }} />
                  {t('transfer.rate.deviceLegend')}
                </span>
                <span className="htdt-legend-item">
                  <span className="htdt-legend-dot" style={{ background: 'var(--info)' }} />
                  {t('transfer.rate.networkLegend')}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Unique Connections */}
        <div className="card card-pad">
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('transfer.connections.heading')}
          </h3>
          <p className="htdt-card-subtitle">
            {t('transfer.connections.subtitle', {
              threshold: hotspot.unique_connections?.threshold ?? HIP134_THRESHOLD,
            })}
          </p>
          {hotspot.unique_connections ? (
            <UniqueConnectionsMeter connections={hotspot.unique_connections} />
          ) : (
            <p className="muted" style={{ fontSize: 13 }} data-testid="connections-no-data">
              {t('transfer.connections.noData')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
